/**
 * Deposit approvals.
 *
 * A deposit is confirmed by matching a bank UTR (or a chain transaction hash)
 * to our reference. The server does the matching: it takes the UTR plus either
 * our reference or the raw bank narration, and finds the deposit itself. This
 * screen exists to give the operator the reference to search their bank
 * statement for, and to carry the UTR back.
 *
 * Two details the operator needs and the UI must not bury:
 *
 *   - Which collection method served the deposit. If money is missing, that is
 *     the account to look in, and the schema guarantees it was recorded.
 *   - The observed amount. A payer who sends a different amount to the one they
 *     requested is common; the server credits what was observed, so the field
 *     is here, defaulted to the requested amount and clearly flagged when the
 *     operator changes it.
 *
 * The UTR is unique across all deposits at the database level, so pasting a UTR
 * that has already been used cannot double-credit — it fails on the constraint.
 */
import { useState, type ReactNode } from 'react';
import { confirmDeposit, expireStaleDeposits, fetchDeposits } from '../api/client.ts';
import type { AdminDeposit } from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { isAmount, format } from '../lib/money.ts';
import { age, absolute, minutesUntil, shortTime } from '../lib/datetime.ts';
import {
  Card,
  EmptyState,
  ErrorState,
  Money,
  StatusBadge,
  TableSkeleton,
} from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { IconClock, IconInbound, IconRefresh } from '../ui/icons.tsx';

const FILTERS = ['pending', 'credited', 'expired', 'all'] as const;
type Filter = (typeof FILTERS)[number];

/** The table's shape, reused by the loading skeleton so nothing jumps. */
const COLUMNS = ['text', 'text', 'num', 'text', 'text', 'text', 'text', 'num', 'num'] as const;

export function Deposits(): ReactNode {
  const [filter, setFilter] = useState<Filter>('pending');
  const [selected, setSelected] = useState<number | null>(null);

  const deposits = useAsync(
    () => fetchDeposits(filter === 'all' ? undefined : filter),
    [filter],
  );
  const confirm = useConfirm();
  const toast = useToast();

  const rows = deposits.data ?? [];
  const active = rows.find((d) => d.depositId === selected) ?? null;

  async function onExpire(): Promise<void> {
    const result = await confirm({
      title: 'Expire stale deposits',
      body: 'Every pending deposit whose window has closed is marked expired and its reserved method headroom is released. No money moves.',
      confirmLabel: 'Expire stale deposits',
    });
    if (!result.confirmed) return;
    try {
      const outcome = await expireStaleDeposits();
      toast(
        `${outcome.expired} deposit${outcome.expired === 1 ? '' : 's'} expired, ${format(outcome.headroomReleased)} of headroom released.`,
      );
      deposits.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not expire deposits.', 'error');
    }
  }

  return (
    <div className="split">
      <div className="stack">
        <div className="toolbar">
          <div className="segmented" role="group" aria-label="Deposit status">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                className={`segment${filter === f ? ' is-active' : ''}`}
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
          <span className="card__spacer" />
          <button type="button" className="btn btn--sm btn--ghost" onClick={onExpire}>
            <IconClock size="sm" />
            Expire stale
          </button>
          <button type="button" className="btn btn--sm btn--ghost" onClick={deposits.reload}>
            <IconRefresh size="sm" />
            Refresh
          </button>
        </div>

        <Card title="Deposits" icon={<IconInbound />} count={rows.length} flush>
          {deposits.error && rows.length === 0 ? (
            <ErrorState
              title="The deposit queue did not load"
              detail={deposits.error}
              onRetry={deposits.reload}
            />
          ) : deposits.loading && rows.length === 0 ? (
            <TableSkeleton columns={COLUMNS} rows={8} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing waiting"
              hint={
                filter === 'pending'
                  ? 'No deposit is holding for a UTR match. A quiet queue is a good queue.'
                  : `No deposit in this window has the status “${filter}”.`
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Ref</th>
                    <th>Player</th>
                    <th className="right">Amount</th>
                    <th>Kind</th>
                    <th>Collection method</th>
                    <th>Claimed UTR</th>
                    <th>Status</th>
                    <th className="right">Age</th>
                    <th className="right">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.depositId}
                      className={`row--clickable${row.depositId === selected ? ' is-selected' : ''}`}
                      onClick={() => setSelected(row.depositId)}
                    >
                      <td className="mono nowrap cell-lead">{row.reference}</td>
                      <td className="nowrap cell-lead">
                        {row.username}
                        <span className="cell-sub">#{row.userId}</span>
                      </td>
                      <td className="right mono">
                        <Money value={row.amount} />
                      </td>
                      <td className="muted">{row.kind}</td>
                      <td className="nowrap muted">{row.methodLabel}</td>
                      <td className="mono nowrap">
                        {row.claimedUtr ?? <span className="muted">—</span>}
                        {row.claimedUtrDuplicate ? (
                          <span
                            className="badge badge--loss"
                            title="Another deposit claims or settled this UTR — review before crediting"
                          >
                            dup
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="right mono muted" title={absolute(row.createdAt)}>
                        {age(row.createdAt)}
                      </td>
                      <td className="right mono muted">{expiryText(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Keyed by deposit so the UTR field re-seeds from each row's claim. */}
      <ConfirmPanel
        key={active?.depositId ?? 'none'}
        deposit={active}
        onDone={deposits.reload}
      />
    </div>
  );
}

function expiryText(row: AdminDeposit): string {
  if (row.status !== 'pending') return shortTime(row.expiresAt);
  const minutes = minutesUntil(row.expiresAt);
  if (minutes === null) return '—';
  return minutes <= 0 ? 'lapsed' : `${minutes}m`;
}

/**
 * The confirmation rail. Kept beside the queue rather than in a modal because
 * the operator is reading a bank statement in another window and needs the
 * reference and the receiving account visible while they type.
 *
 * When the player has already claimed a UTR it pre-fills the field, so a
 * clean match is: select the row, check the claim against the statement,
 * confirm — one click, nothing retyped.
 */
function ConfirmPanel({
  deposit,
  onDone,
}: {
  deposit: AdminDeposit | null;
  onDone: () => void;
}): ReactNode {
  const [utr, setUtr] = useState(() =>
    deposit?.status === 'pending' ? (deposit.claimedUtr ?? '') : '',
  );
  const [observed, setObserved] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  if (!deposit) {
    return (
      <Card title="Confirm a deposit" icon={<IconInbound />}>
        <EmptyState
          compact
          icon={<IconInbound size="lg" />}
          title="No deposit selected"
          hint="Pick a row. Its reference is what to search the bank statement for, and the UTR comes back here."
        />
      </Card>
    );
  }

  const amountValid = observed.trim() === '' || isAmount(observed.trim());
  const differs = observed.trim() !== '' && amountValid && observed.trim() !== deposit.amount;

  async function onConfirm(): Promise<void> {
    if (!deposit) return;
    const trimmedUtr = utr.trim();
    if (trimmedUtr.length < 4) {
      toast('Enter the bank UTR or the chain transaction hash.', 'error');
      return;
    }
    if (!amountValid) {
      toast('The observed amount must be a decimal with up to four places.', 'error');
      return;
    }

    const credited = observed.trim() === '' ? deposit.amount : observed.trim();

    const result = await confirm({
      title: 'Credit this deposit',
      intent: 'default',
      body: 'This credits the player’s balance immediately. The ledger entry is keyed to the deposit, so a repeat of this action moves no money — but the wrong player credited is a manual correction.',
      confirmLabel: 'Credit player',
      facts: [
        { label: 'Player', value: `${deposit.username} #${deposit.userId}` },
        { label: 'Reference', value: deposit.reference },
        { label: 'Requested', value: <Money value={deposit.amount} currency /> },
        { label: 'Crediting', value: <Money value={credited} currency /> },
        { label: 'UTR', value: trimmedUtr },
        { label: 'Received into', value: deposit.methodLabel },
      ],
      ...(differs
        ? {
            note: {
              label: 'Why the amount differs',
              required: true,
              hint: 'The observed amount is what gets credited. Record why it is not the requested amount.',
            },
          }
        : {}),
    });

    if (!result.confirmed) return;

    setBusy(true);
    try {
      const outcome = await confirmDeposit({
        utr: trimmedUtr,
        reference: deposit.reference,
        ...(observed.trim() === '' ? {} : { observedAmount: observed.trim() }),
      });
      toast(
        outcome.alreadyCredited
          ? `Already credited — no money moved. Balance ${format(outcome.balance)}.`
          : `Credited ${format(outcome.creditedAmount)}. Balance is now ${format(outcome.balance)}.`,
      );
      setUtr('');
      setObserved('');
      onDone();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not confirm the deposit.', 'error');
    } finally {
      setBusy(false);
    }
  }

  const payable = deposit.status === 'pending';

  return (
    <Card title="Confirm a deposit" icon={<IconInbound />}>
      <div className="kv kv--spaced">
        <span className="kv__k">Player</span>
        <span className="kv__v">
          {deposit.username} <span className="muted">#{deposit.userId}</span>
        </span>
        <span className="kv__k">Amount</span>
        <span className="kv__v kv__v--lead">
          <Money value={deposit.amount} currency />
        </span>
        <span className="kv__k">Reference</span>
        <span className="kv__v num">{deposit.reference}</span>
        <span className="kv__k">Status</span>
        <span className="kv__v">
          <StatusBadge status={deposit.status} />
        </span>
        <span className="kv__k">Method</span>
        <span className="kv__v">{deposit.methodLabel}</span>
        <span className="kv__k">Entity</span>
        <span className="kv__v">{deposit.methodOwnerEntity}</span>
        <span className="kv__k">Pay to</span>
        <span className="kv__v mono-code">{deposit.address ?? '—'}</span>
        <span className="kv__k">Opened</span>
        <span className="kv__v" title={absolute(deposit.createdAt)}>
          {shortTime(deposit.createdAt)}
        </span>
        <span className="kv__k">Expires</span>
        <span className="kv__v" title={absolute(deposit.expiresAt)}>
          {shortTime(deposit.expiresAt)}
        </span>
        {deposit.claimedUtr ? (
          <>
            <span className="kv__k">Player claims</span>
            <span className="kv__v mono-code">
              {deposit.claimedUtr}
              {deposit.claimedUtrDuplicate ? (
                <span
                  className="badge badge--loss"
                  title="Another deposit claims or settled this UTR"
                >
                  dup
                </span>
              ) : null}
            </span>
          </>
        ) : null}
        {deposit.utrReference ? (
          <>
            <span className="kv__k">UTR</span>
            <span className="kv__v mono-code">{deposit.utrReference}</span>
          </>
        ) : null}
      </div>

      {deposit.claimedUtrDuplicate && payable ? (
        <p className="field__error">
          Another deposit claims or settled this UTR. One bank credit funds one deposit — check the
          statement before crediting either.
        </p>
      ) : null}

      {payable ? (
        <>
          <label className="field">
            <span className="field__label">Bank UTR / transaction hash</span>
            <input
              className="input num"
              value={utr}
              onChange={(event) => setUtr(event.target.value)}
              placeholder="418923004471"
            />
            <span className="field__hint">
              {deposit.claimedUtr && utr === deposit.claimedUtr
                ? 'Pre-filled from the player’s claim — verify it against the bank statement before crediting.'
                : 'Unique across every deposit — a UTR already used cannot credit twice.'}
            </span>
          </label>

          <label className="field">
            <span className="field__label">Observed amount</span>
            <input
              className="input num"
              value={observed}
              onChange={(event) => setObserved(event.target.value)}
              placeholder={deposit.amount}
            />
            {!amountValid ? (
              <span className="field__error">Use a decimal with up to four places.</span>
            ) : differs ? (
              <span className="field__error">
                Differs from the requested <Money value={deposit.amount} currency />. This amount is
                what gets credited.
              </span>
            ) : (
              <span className="field__hint">Leave blank to credit the requested amount.</span>
            )}
          </label>

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? 'Crediting…' : 'Confirm against UTR'}
          </button>
        </>
      ) : (
        <p className="field__hint">
          This deposit is {deposit.status}. Only a pending deposit can be confirmed.
        </p>
      )}
    </Card>
  );
}
